import React, { useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let LABELS_LOADED = false;
async function loadMBLabelsOnce() {
  if (LABELS_LOADED) return;
  LABELS_LOADED = true;
  try {
    const res = await axios.get(`${API}/mb-config`);
    if (res.data?.labels) {
      localStorage.setItem('mb_labels', JSON.stringify(res.data.labels));
      window.dispatchEvent(new Event('mb-labels-updated'));
    }
  } catch {}
}

/**
 * Small colored pill showing which MB Setup a customer belongs to.
 * Renders nothing when value is empty/null.
 *
 * Props:
 *   value: 'MB1' | 'MB2' | 'MB3' | 'MB4' | 'MB5' | null
 *   size:  'xs' | 'sm' (default 'xs')
 */
const MB_COLORS = {
  MB7:  'bg-emerald-100 text-emerald-700 border-emerald-300',
  MB8:  'bg-sky-100 text-sky-700 border-sky-300',
  MB9:  'bg-amber-100 text-amber-700 border-amber-300',
  MB10: 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-300',
  MB11: 'bg-rose-100 text-rose-700 border-rose-300',
};

// Fix-batch-4 #2 — newly created MBs (MB12, MB13, …) get a deterministic
// colour from this palette so their badge isn't a dull gray everywhere.
const MB_COLOR_PALETTE = [
  'bg-teal-100 text-teal-700 border-teal-300',
  'bg-indigo-100 text-indigo-700 border-indigo-300',
  'bg-lime-100 text-lime-700 border-lime-300',
  'bg-orange-100 text-orange-700 border-orange-300',
  'bg-cyan-100 text-cyan-700 border-cyan-300',
  'bg-violet-100 text-violet-700 border-violet-300',
];
const colorForMB = (value) => {
  if (MB_COLORS[value]) return MB_COLORS[value];
  const n = parseInt(String(value).replace(/\D/g, ''), 10);
  if (!Number.isNaN(n)) return MB_COLOR_PALETTE[n % MB_COLOR_PALETTE.length];
  return 'bg-gray-100 text-gray-700 border-gray-300';
};

const MBSetupBadge = ({ value, size = 'xs', className = '' }) => {
  // Fetch labels lazily on first badge render + refresh on custom event
  const [, force] = React.useReducer((x) => x + 1, 0);
  useEffect(() => {
    loadMBLabelsOnce();
    const listener = () => force();
    window.addEventListener('mb-labels-updated', listener);
    return () => window.removeEventListener('mb-labels-updated', listener);
  }, []);

  if (!value) return null;
  const colorClass = colorForMB(value);
  const sizeClass =
    size === 'sm'
      ? 'text-[11px] px-2 py-0.5'
      : 'text-[9px] px-1.5 py-0.5';

  // Show custom label if configured (cached in localStorage from MBConfig page)
  let display = value;
  try {
    const cached = JSON.parse(localStorage.getItem('mb_labels') || '{}');
    if (cached[value]) display = cached[value];
  } catch {}

  return (
    <span
      className={`inline-flex items-center font-bold rounded-full border ${colorClass} ${sizeClass} ${className}`}
      data-testid={`mb-badge-${value}`}
      title={value}
    >
      {display}
    </span>
  );
};

export const MB_SETUPS = ['MB7', 'MB8', 'MB9', 'MB10', 'MB11'];

export default MBSetupBadge;
